import { useMemo, useState, FC, Fragment, useEffect } from 'react';
import type { Trade } from '../../types';
import { TopFilters } from '../filters/TopFilters';
import { DayJournalPage } from './DayJournalPage';
import CreateCustomEntryModal from '../ui/CreateCustomEntryModal';
import { useCanonicalStatisticsTrades } from '../../hooks/useCanonicalStatisticsTrades';
import StandardPageHeader from './StandardPageHeader';
import { buildMonthGridFromTrades, getTradeTimestampMs } from '../../lib/trades/authoritativeTradeData';

// --- Helper Types ---
type DayStat = { pnl: number; trades: number };
type CalendarCell = { d: Date; inMonth: boolean; stat: DayStat };

// --- Mock Utilities & Data Simulation ---

const fmtMoney = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    signDisplay: 'auto',
  }).format(amount);
};

// --- Component: MonthCard ---

type MonthCardProps = {
  label: string;
  cells: CalendarCell[];
  onOpen: () => void;
  onDayClick?: (d: Date) => void;
};

const MonthCard: FC<MonthCardProps> = ({ label, cells, onOpen, onDayClick }) => {
  const monthlyPnL = cells.filter((c) => c.inMonth).reduce((a, c) => a + c.stat.pnl, 0);
  const totalTrades = cells.filter((c) => c.inMonth).reduce((a, c) => a + c.stat.trades, 0);
  const pnlText = monthlyPnL > 0 ? `+${fmtMoney(monthlyPnL)}` : fmtMoney(monthlyPnL);
  const pnlClass = monthlyPnL >= 0 ? 'text-emerald-600' : 'text-red-600';

  return (
    <div className="rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
      <div className="px-4 pt-3 pb-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="text-lg font-semibold text-zinc-800 dark:text-zinc-200">{label}</div>
          <div className="h-6 w-px bg-zinc-200 dark:bg-zinc-700" />
          <div className={`text-sm font-medium ${pnlClass}`}>{pnlText}</div>
          <div className="h-6 w-px bg-zinc-200 dark:bg-zinc-700" />
          <div className="text-sm text-zinc-500 dark:text-zinc-400">{totalTrades} Total Trades</div>
        </div>
        <button onClick={onOpen} className="flex items-center justify-center text-sm rounded-xl border border-zinc-300 dark:border-zinc-700 px-3 h-9 leading-none bg-zinc-100 dark:bg-zinc-800 hover:bg-blue-100 dark:hover:bg-blue-900/50 hover:text-blue-600 dark:hover:text-blue-300 hover:border-blue-300 dark:hover:border-blue-700">Open</button>
      </div>

      <div className="grid grid-cols-7 text-sm text-zinc-500 dark:text-zinc-400 px-3 pb-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="px-1 py-0.5 text-center">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-px rounded-xl overflow-hidden border border-zinc-300 dark:border-zinc-700 bg-zinc-300/90 dark:bg-zinc-700/80 mx-3 mb-3">
        {cells.map((c, i) => {
          // Out-of-month cells (leading/trailing)
          if (!c.inMonth)
            return (
              <div key={i} className="bg-white dark:bg-zinc-900 min-h-[78px] p-1 flex items-center justify-center">
                <div className="text-[11px] font-medium text-zinc-300 dark:text-zinc-700">{c.d.getDate()}</div>
              </div>
            );

          const hasTrades = c.stat.trades > 0;

          // Days with no trades: show muted zinc background and 'No Trades'
          if (!hasTrades) {
            return (
              <div key={i} className="min-h-[78px] p-1 flex flex-col items-center justify-center gap-1 border-none text-center bg-gray-50 dark:bg-zinc-800">
                <div className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{c.d.getDate()}</div>
                <div className="text-[9px] uppercase tracking-wide text-zinc-700 dark:text-zinc-300 mt-1">No Trades</div>
              </div>
            );
          }

          const pos = c.stat.pnl > 0;
          const square = pos
            ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300'
            : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400';
          const tradeLabel = c.stat.trades === 1 ? 'trade' : 'trades';
          return (
            <button
              key={i}
              onClick={() => onDayClick?.(c.d)}
              className={`${square} min-h-[78px] p-1 flex flex-col items-center justify-center gap-1 border-none text-center`}
              type="button"
            >
              <div className="w-full h-full flex flex-col items-center justify-center px-1 py-2">
                <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{c.d.getDate()}</div>
                <div className="text-[9px] uppercase tracking-wide text-zinc-700 dark:text-zinc-300 mt-1">{c.stat.trades} {tradeLabel}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// --- Component: DetailedMonthModal ---

type DayCellProps = { cell: CalendarCell; onDayClick: (date: Date) => void };
const DayCell: FC<DayCellProps> = ({ cell, onDayClick }) => {
  const hasTrades = cell.stat.trades > 0;

  const handleClick = () => {
    if (hasTrades && cell.inMonth) {
        onDayClick(cell.d);
    }
  };

  if (!cell.inMonth) {
    return (
      <div className="bg-white dark:bg-zinc-900 min-h-[100px] p-2 text-zinc-300 dark:text-zinc-700 text-sm">
        {cell.d.getDate()}
      </div>
    );
  }

  const pnlValue = cell.stat.pnl;
  const pos = pnlValue > 0;
  const neg = pnlValue < 0;
  const amountClass = pos ? 'text-emerald-600' : neg ? 'text-red-600' : 'text-zinc-800 dark:text-zinc-200';
  const backgroundClass = hasTrades ? (pos ? 'bg-emerald-50' : neg ? 'bg-rose-50' : 'bg-white') : 'bg-white';
  const hoverClass = hasTrades ? (pos ? 'hover:bg-emerald-100/60' : neg ? 'hover:bg-rose-100/60' : 'hover:bg-zinc-50') : '';

  return (
   <button
     onClick={handleClick}
     disabled={!hasTrades}
     className={`${backgroundClass} dark:bg-zinc-900 min-h-[100px] p-2 text-center w-full block ${hasTrades ? `cursor-pointer ${hoverClass} dark:hover:bg-zinc-800/50` : ''}`}
   >
        <div className="text-xs font-semibold text-zinc-500 dark:text-zinc-400">{cell.d.getDate()}</div>
        {hasTrades ? (
          <>
              <div className={`mt-2 text-base font-semibold ${amountClass}`}>{fmtMoney(cell.stat.pnl)}</div>
              <div className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-1">
                {cell.stat.trades} {cell.stat.trades === 1 ? 'trade' : 'trades'}
              </div>
          </>
        ) : (
          <div className="text-xs text-zinc-400 dark:text-zinc-500 mt-2">No Trades</div>
        )}
     </button>
  );
};

type WeekRowProps = { week: CalendarCell[]; onDayClick: (date: Date) => void; };
const WeekRow: FC<WeekRowProps> = ({ week, onDayClick }) => {
  const weekPnL = week.filter((c) => c.inMonth).reduce((a, c) => a + c.stat.pnl, 0);
  const weekTrades = week.filter((c) => c.inMonth).reduce((a, c) => a + c.stat.trades, 0);

  return (
    <>
      {week.map((c, i) => <DayCell key={i} cell={c} onDayClick={onDayClick} />)}
      <div className="bg-white dark:bg-zinc-900 min-h-[100px] p-2 flex flex-col items-center justify-center text-center">
        <div className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400 mb-1">WEEK {week.findIndex(c => c.inMonth) >= 0 ? Math.ceil((week.find(c => c.inMonth)?.d.getDate() || 1) / 7) : 1}:</div>
        <div className={`text-sm font-semibold ${weekPnL >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmtMoney(weekPnL)}</div>
        <div className="text-[11px] text-zinc-500 dark:text-zinc-400">
          {weekTrades} {weekTrades === 1 ? 'trade' : 'trades'}
        </div>
        {/* Per-week 'Journal' button removed as requested */}
      </div>
    </>
  );
};

type DetailedMonthModalProps = { year: number; month: number; cells: CalendarCell[]; onClose: () => void; onDayClick: (date: Date) => void; };
const DetailedMonthModal: FC<DetailedMonthModalProps> = ({ year, month, cells, onClose, onDayClick }) => {
  const { weeks, monthlyPnL, monthLabel } = useMemo(() => {
    const weeks: CalendarCell[][] = [];
    for (let i = 0; i < cells.length; i += 7) {
      weeks.push(cells.slice(i, i + 7));
    }
    const monthlyPnL = cells.filter((c) => c.inMonth).reduce((a, c) => a + c.stat.pnl, 0);
    const monthLabel = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(new Date(year, month, 1));
    return { weeks, monthlyPnL, monthLabel };
  }, [year, month, cells]);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-6xl max-h-[90vh] overflow-auto rounded-2xl bg-white dark:bg-zinc-900 shadow-xl border border-zinc-200 dark:border-zinc-800">
        <div className="grid grid-cols-3 items-center px-6 py-4 border-b border-gray-200 dark:border-zinc-800">
          <div>
            <div className="text-2xl font-bold">{monthLabel}</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-zinc-500 dark:text-zinc-400">Monthly P&L</div>
            <div className={`text-2xl font-bold ${monthlyPnL >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmtMoney(monthlyPnL)}</div>
          </div>
          <div className="flex justify-end">
            <button aria-label="Close month" onClick={onClose} className="h-9 w-9 rounded-lg border border-zinc-300 dark:border-zinc-700 flex items-center justify-center bg-white dark:bg-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-700">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          </div>
        </div>

        <div className="p-4">
        <div className="grid grid-cols-8 gap-px rounded-xl overflow-hidden border border-zinc-300 dark:border-zinc-700 bg-zinc-300/80 dark:bg-zinc-700/80">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Total'].map((h) => (
              <div key={h} className="bg-white dark:bg-zinc-900 text-sm font-medium text-zinc-500 dark:text-zinc-400 px-3 py-2 text-center">{h}</div>
            ))}

            {weeks.map((week, wi) => (
              <Fragment key={wi}>
                <WeekRow week={week} onDayClick={onDayClick} />
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Main Calendar Page Component ---

type ActiveModal = { year: number; month: number; };
type View = 'calendar' | 'journal';

export function CalendarPage({ onNavigateToJournalEntry, onCreateNewEntry }: {
  onNavigateToJournalEntry?: (date: Date) => void;
  onCreateNewEntry?: () => void;
}) {
  const authoritativeTrades = useCanonicalStatisticsTrades();
  const [activeModal, setActiveModal] = useState<ActiveModal | null>(null);
  const [currentView, setCurrentView] = useState<View>('calendar');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [createOpen, setCreateOpen] = useState(false);
  const currentYearDefault = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYearDefault);
  const years = useMemo(() => {
    const uniqueYears = new Set<number>();
    authoritativeTrades.forEach((trade) => {
      const timestampMs = getTradeTimestampMs(trade as Trade);
      if (timestampMs != null) uniqueYears.add(new Date(timestampMs).getFullYear());
    });
    return Array.from(uniqueYears).sort((a, b) => a - b);
  }, [authoritativeTrades]);
  const monthCells = useMemo(
    () => Array.from({ length: 12 }, (_, monthIndex) => buildMonthGridFromTrades(authoritativeTrades as Trade[], year, monthIndex) as CalendarCell[]),
    [authoritativeTrades, year]
  );
  const monthFormatter = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' });

  useEffect(() => {
    if (!years.length) return;
    if (!years.includes(year)) {
      setYear(years[0]);
    }
  }, [years, year]);

  const handleDayClick = (date: Date) => {
    setActiveModal(null);
    if (onNavigateToJournalEntry) {
      onNavigateToJournalEntry(date);
      return;
    }

    setSelectedDate(date);
    setCurrentView('journal');
  };

  const handleNavigateDate = (newDate: Date) => {
    setSelectedDate(newDate);
  }

  const handleBackToCalendar = () => {
      setCurrentView('calendar');
  }

  if (currentView === 'journal') {
      return <DayJournalPage date={selectedDate} onBack={handleBackToCalendar} onNavigateDate={handleNavigateDate} />;
  }

  return (
    <>
      <StandardPageHeader title="Calendar" subtitle="Overview of monthly P&L and trade counts." />

      <div className="h-2" />

      <TopFilters />

      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-2">
          {years.map((y) => (
            <button
              key={y}
              onClick={() => setYear(y)}
              className={
                `px-4 h-10 rounded-xl text-sm font-medium border transition-colors flex items-center justify-center ` +
                (y === year
                  ? 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200'
                  : 'bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 border-zinc-200 dark:border-zinc-800')
              }
              type="button"
            >
              {y}
            </button>
          ))}
        </div>

        <div>
          <button
            onClick={() => setCreateOpen(true)}
            className="px-3 h-10 rounded-xl border flex items-center gap-2 bg-blue-600 dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 text-white dark:text-zinc-300"
          >
            New Entry
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-10">
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((m) => (
          <MonthCard
            key={m}
            label={monthFormatter.format(new Date(year, m, 1))}
            cells={monthCells[m] ?? []}
            onOpen={() => setActiveModal({ year, month: m })}
            onDayClick={handleDayClick}
          />
        ))}
      </div>

      {activeModal && (
        <DetailedMonthModal
          year={activeModal.year}
          month={activeModal.month}
          cells={monthCells[activeModal.month] ?? []}
          onClose={() => setActiveModal(null)}
          onDayClick={handleDayClick}
        />
      )}

      <CreateCustomEntryModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New Entry"
        onCreate={(payload) => {
          console.log('Created new entry:', payload);
          onCreateNewEntry?.();
          setCreateOpen(false);
        }}
      />
      <div className="h-4 md:h-4" aria-hidden="true" />
    </>
  );
}
